import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  ThemeProvider,
  createTheme,
  styled,
} from "@mui/material";

const OtpInput = styled(TextField)(({ theme }) => ({
  width: "48px !important",
  "& input": {
    textAlign: "center",
    fontSize: "24px",
    padding: "8px",
  },
}));

const theme = createTheme({
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#e0e0e0",
            },
            "&:hover fieldset": {
              borderColor: "#bdbdbd",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#1976d2",
              borderWidth: "1px",
            },
          },
        },
      },
    },
  },
});

interface OTPVerificationProps {
  onComplete: (otp: string) => void;
  error?: string;
  resendOtp?: () => void;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({
  onComplete,
  error,
  resendOtp,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if OTP is complete
    if (newOtp.every((digit) => digit !== "")) {
      onComplete(newOtp.join(""));
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain").trim();

    if (/^\d{6}$/.test(pasteData)) {
      const pasteDigits = pasteData.split("");
      const newOtp = [...otp];

      pasteDigits.forEach((digit, i) => {
        if (i < 6) {
          newOtp[i] = digit;
        }
      });

      setOtp(newOtp);
      onComplete(newOtp.join(""));

      // Focus the last input
      const lastIndex = Math.min(5, pasteDigits.length - 1);
      inputRefs.current[lastIndex]?.focus();
    }
  };

  useEffect(() => {
    // Focus the first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Enter verification code
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            margin: "16px 0",
          }}
          onPaste={handlePaste}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <OtpInput
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              variant="outlined"
              value={otp[index]}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(index, e)
              }
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              autoComplete="one-time-code"
            />
          ))}
        </Box>

        {error && (
          <Typography
            variant="body2"
            sx={{ color: "#d32f2f", marginTop: "4px" }}
          >
            {error}
          </Typography>
        )}

        {resendOtp && (
          <Box sx={{ marginTop: 2, textAlign: "right" }}>
            <Typography
              variant="body2"
              color="primary"
              onClick={resendOtp}
              sx={{ cursor: "pointer", textDecoration: "underline" }}
            >
              Resend OTP
            </Typography>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default OTPVerification;
